import { LogLevel, type Configuration } from "@azure/msal-browser";

/**
 * MSAL setup. None of these values are secrets — a public client cannot keep
 * one, which is why this uses Authorization Code with PKCE rather than a
 * client secret.
 */

/**
 * Names of any configuration this build is missing.
 *
 * Collected rather than thrown. Throwing here happens while the module is being
 * imported, before React exists, so the only symptom is a blank white page and
 * an error buried in the console — which is a miserable thing to debug. The
 * entry point checks this and renders something that says what is wrong.
 */
export const missingConfig: string[] = [];

function read(name: string, value: string | undefined): string {
  if (!value) {
    missingConfig.push(name);
    return "";
  }
  return value;
}

const tenantId = read("VITE_ENTRA_TENANT_ID", import.meta.env.VITE_ENTRA_TENANT_ID);
const clientId = read("VITE_ENTRA_CLIENT_ID", import.meta.env.VITE_ENTRA_CLIENT_ID);

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    // Must exactly match a redirect URI on the app registration.
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // localStorage: one sign-in serves every tab of this app in the browser, and
    // survives closing it (Jeremy, 30 Oct 2026, punch list F131/S10/S11/S15:
    // "We don't share computers"). Until then sessionStorage, so the token died
    // with the tab and every new tab -- an email link, a Teams link, a link from
    // the other app -- stopped on "Sign in with Microsoft". Each app is its own
    // origin, so each still signs in once per browser.
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
      },
    },
  },
};

/** The scope the API checks for. Requested when acquiring an access token. */
export const apiScope = `api://${clientId}/access_as_user`;

/** Scopes requested at sign-in. */
export const loginRequest = {
  scopes: [apiScope],
};
